// src/pages/WelcomePage.js
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useFirebase } from '../contexts/FirebaseContext';
import '../welcome.css';
import FirebaseAuthModal from '../components/FirebaseAuthModal';
import { FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import apiClient from '../utils/apiClient';

// Import images
import slide5Transparent from '../images/slide5-final-transparent.png';
import slide4Final from '../images/slide4-final.png';
import sentences2 from '../images/sentences2.png';
import dailystory from '../images/dailystory.png';
import slide6 from '../images/sllide6.png';
import audioPlayer1 from '../images/audio-player1.png';

const WelcomePage = ({ onAuthenticated: onAuthenticatedCallback }) => {
    const [modal, setModal] = useState(null);
    const [signupStep, setSignupStep] = useState(1);
    const [signupData, setSignupData] = useState({
        learningLanguage: "",
        spanishRegion: "",
        languageLevel: "",
        daysOfReview: "",
        repeatPercentage: "",
        dailyStoryRepeatNumber: "",
        username: "",
        chineseCharacterType: ""
    });
    const [usernameStatus, setUsernameStatus] = useState({
        checking: false,
        available: null,
        message: ""
    });

    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated } = useFirebase();

    // Check URL parameters on component mount
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const authMode = params.get('auth');

        if (authMode === 'login') {
            setModal('auth');
        } else if (authMode === 'signup') {
            setModal('signup');
            setSignupStep(1);
        }
    }, [location.search]);

    const openModal = useCallback((type) => {
        if (!isAuthenticated) {
            if (type === 'signup') {
                setModal('signup');
                setSignupStep(1);
                // Add URL parameter
                navigate('?auth=signup', { replace: true });
            } else {
                setModal('auth');
                // Add URL parameter
                navigate('?auth=login', { replace: true });
            }
        } else {
            navigate('/');
        }
    }, [isAuthenticated, navigate]);

    const closeModal = useCallback(() => {
        setModal(null);
        setSignupStep(1);
        // Remove URL parameter
        navigate(location.pathname, { replace: true });
    }, [navigate, location.pathname]);

    const handleAuthenticated = useCallback(
        async (authResult) => {
            console.log('Authenticated!', authResult);
            if (authResult?.accessToken) {
                localStorage.setItem('access_token', authResult.accessToken);
            }
            if (authResult?.idToken) {
                localStorage.setItem('id_token', authResult.idToken);
            }

            if (modal === 'signup' || (modal === 'auth' && signupStep === 6)) {
                // Store settings in localStorage first
                localStorage.setItem('learningLanguage', signupData.learningLanguage);
                localStorage.setItem('languageLevel', signupData.languageLevel);
                localStorage.setItem('daysOfReview', signupData.daysOfReview);
                localStorage.setItem('repeatPercentage', signupData.repeatPercentage);
                localStorage.setItem('dailyStoryRepeatNumber', signupData.dailyStoryRepeatNumber);

                if (signupData.learningLanguage === 'Spanish') {
                    localStorage.setItem('spanishRegion', signupData.spanishRegion);
                }

                if (signupData.learningLanguage === 'Chinese') {
                    localStorage.setItem('chineseCharacterType', signupData.chineseCharacterType);
                }

                // Also save to the backend API for persistence
                try {
                    const response = await apiClient.put('/api/settings', { // Use relative path
                        learningLanguage: signupData.learningLanguage,
                        languageLevel: signupData.languageLevel,
                        daysOfReview: signupData.daysOfReview,
                        repeatPercentage: signupData.repeatPercentage,
                        dailyStoryRepeatNumber: signupData.dailyStoryRepeatNumber,
                        spanishRegion: signupData.learningLanguage === 'Spanish' ? signupData.spanishRegion : '',
                        chineseCharacterType: signupData.learningLanguage === 'Chinese' ? signupData.chineseCharacterType : ''
                    });

                    if (response.ok) {
                        console.log('Settings saved to backend successfully');
                    } else {
                        console.error('Failed to save settings to backend', response.status);
                    }
                } catch (error) {
                    console.error('Error saving settings to backend:', error);
                }
            }

            if (onAuthenticatedCallback) {
                onAuthenticatedCallback();
            }
            // Clear URL parameters and navigate to home
            navigate('/');
        },
        [onAuthenticatedCallback, navigate, modal, signupData]
    );

    const handleSignupChange = (key, value) => {
        const newData = {
            ...signupData,
            [key]: value,
        };
        setSignupData(newData);

        // If username is being updated, check availability
        if (key === 'username') {
            // Store it in localStorage if not empty
            if (value.trim()) {
                localStorage.setItem('username', value.trim());
            }

            // Don't check if username is empty
            if (!value.trim()) {
                setUsernameStatus({
                    checking: false,
                    available: null,
                    message: ""
                });
                return;
            }

            // Set checking status
            setUsernameStatus({
                checking: true,
                available: null,
                message: "Checking availability..."
            });

            // Debounce the API call
            const timeoutId = setTimeout(async () => {
                try {
                    // apiClient.get returns the response object directly, similar to fetch
                    const response = await apiClient.get(`/api/auth/check-username?username=${encodeURIComponent(value.trim())}`);
                    const data = await response.json(); // This line can remain as is if response from apiClient.get is the raw response

                    if (response.ok) {
                        setUsernameStatus({
                            checking: false,
                            available: data.available,
                            message: data.available ? "" : "Username is already taken"
                        });
                    } else {
                        setUsernameStatus({
                            checking: false,
                            available: null,
                            message: data.error || "Failed to check username"
                        });
                    }
                } catch (error) {
                    console.error("Error checking username:", error);
                    setUsernameStatus({
                        checking: false,
                        available: null,
                        message: "Error checking username"
                    });
                }
            }, 500); // 500ms debounce
        } else if (key === 'learningLanguage') {
            if (value !== 'Spanish') {
                newData.spanishRegion = '';
            }
            if (value !== 'Chinese') {
                newData.chineseCharacterType = '';
            } else if (!newData.chineseCharacterType) {
                // Set default Chinese character type
                newData.chineseCharacterType = 'Simplified';
            }
        }
    };

    const nextStep = () => {
        setSignupStep(signupStep + 1);
    };

    const prevStep = () => {
        if (signupStep > 1) {
            setSignupStep(signupStep - 1);
        }
    };

    const renderSignupContent = () => {
        switch (signupStep) {
            case 1:
                return (
                    <div className="signup-step">
                        <h2>Select the language you'd like to start with...</h2>
                        <select
                            className="signup-settings-select"
                            value={signupData.learningLanguage}
                            onChange={(e) => {
                                const language = e.target.value;
                                if (language !== "") {
                                    handleSignupChange('learningLanguage', language);
                                }
                            }}
                        >
                            <option value="" disabled>Select language</option>
                            <option value="Chinese">Chinese</option>
                            <option value="French">French</option>
                            <option value="Italian">Italian</option>
                            <option value="Japanese">Japanese</option>
                            <option value="Korean">Korean</option>
                            <option value="Spanish">Spanish</option>
                        </select>

                        {signupData.learningLanguage === 'Spanish' && (
                            <>
                                <h3>Choose the region...</h3>
                                <select
                                    className="signup-settings-select"
                                    value={signupData.spanishRegion}
                                    onChange={(e) => handleSignupChange('spanishRegion', e.target.value)}
                                >
                                    <option value="" disabled>Select region</option>
                                    <option value="Spain">Spain</option>
                                    <option value="Latin America">Latin America</option>
                                </select>
                            </>
                        )}

                        {signupData.learningLanguage === 'Chinese' && (
                            <>
                                <h3>Choose the character type...</h3>
                                <select
                                    className="signup-settings-select"
                                    value={signupData.chineseCharacterType}
                                    onChange={(e) => handleSignupChange('chineseCharacterType', e.target.value)}
                                >
                                    <option value="" disabled>Select character type</option>
                                    <option value="Simplified">Simplified Chinese</option>
                                    <option value="Traditional">Traditional Chinese</option>
                                </select>
                            </>
                        )}

                        <div className="button-group">
                            {/* No back button for step 1 */}
                            <button
                                className="continue-button full-rounded"
                                onClick={nextStep}
                                disabled={
                                    !signupData.learningLanguage ||
                                    (signupData.learningLanguage === 'Spanish' && !signupData.spanishRegion) ||
                                    (signupData.learningLanguage === 'Chinese' && !signupData.chineseCharacterType)
                                }
                            >
                                Continue <FaArrowRight />
                            </button>
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="signup-step">
                        <h2>Select your current level...</h2>
                        <select
                            className="signup-settings-select"
                            value={signupData.languageLevel}
                            onChange={(e) => handleSignupChange('languageLevel', e.target.value)}
                        >
                            <option value="" disabled>Select level</option>
                            <option value="Beginner">Beginner</option>
                            <option value="Intermediate">Intermediate</option>
                            <option value="Advanced">Advanced</option>
                        </select>

                        <div className="button-group">
                            <button className="back-button" onClick={prevStep}>
                                <FaArrowLeft />
                            </button>
                            <button
                                className="continue-button"
                                onClick={nextStep}
                                disabled={!signupData.languageLevel}
                            >
                                Continue <FaArrowRight />
                            </button>
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="signup-step">
                        <h2>Select how many days you'd like to repeat your sentences...</h2>
                        <p className="signup-note">
                            <i>Everyday, you'd be repeating the previous day's audio files as well. To not overwhelm yourself,
                                I suggest max 3-4 days to maintain balance between new content and review.</i>
                        </p>
                        <select
                            className="signup-settings-select"
                            value={signupData.daysOfReview}
                            onChange={(e) => handleSignupChange('daysOfReview', parseInt(e.target.value))}
                        >
                            <option value="" disabled>Select days</option>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                <option key={num} value={num}>{num}</option>
                            ))}
                        </select>

                        <div className="button-group">
                            <button className="back-button" onClick={prevStep}>
                                <FaArrowLeft />
                            </button>
                            <button
                                className="continue-button"
                                onClick={nextStep}
                                disabled={!signupData.daysOfReview}
                            >
                                Continue <FaArrowRight />
                            </button>
                        </div>
                    </div>
                );

            case 4:
                return (
                    <div className="signup-step">
                        <h2>Select a % of new sentences that will contain your saved words...</h2>
                        <p className="signup-note">
                            <i>The words that you encounter and save in Daily Stories and your Chat with Bojee will appear in new sentences to reinforce your learning. Select a % of sentences to feature your words.</i>
                        </p>
                        <select
                            className="signup-settings-select"
                            value={signupData.repeatPercentage}
                            onChange={(e) => handleSignupChange('repeatPercentage', parseInt(e.target.value))}
                        >
                            <option value="" disabled>Select percentage</option>
                            {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(num => (
                                <option key={num} value={num}>{num}%</option>
                            ))}
                        </select>

                        <div className="button-group">
                            <button className="back-button" onClick={prevStep}>
                                <FaArrowLeft />
                            </button>
                            <button
                                className="continue-button"
                                onClick={nextStep}
                                disabled={!signupData.repeatPercentage && signupData.repeatPercentage !== 0}
                            >
                                Continue <FaArrowRight />
                            </button>
                        </div>
                    </div>
                );

            case 5:
                return (
                    <div className="signup-step">
                        <h2>Select the # of words you save in your daily stories to reappear in future ones...</h2>
                        <p className="signup-note">
                            <i>In daily stories, you can click on any word to look up its definition and save it to your collection.
                                All future stories will highlight the words you've saved to reinforce your learning.
                                Recommended: &lt;5</i>
                        </p>
                        <select
                            className="signup-settings-select"
                            value={signupData.dailyStoryRepeatNumber}
                            onChange={(e) => handleSignupChange('dailyStoryRepeatNumber', parseInt(e.target.value))}
                        >
                            <option value="" disabled>Select number</option>
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                <option key={num} value={num}>{num}</option>
                            ))}
                        </select>

                        <div className="button-group">
                            <button className="back-button" onClick={prevStep}>
                                <FaArrowLeft />
                            </button>
                            <button
                                className="continue-button"
                                onClick={nextStep}
                                disabled={!signupData.dailyStoryRepeatNumber && signupData.dailyStoryRepeatNumber !== 0}
                            >
                                Continue <FaArrowRight />
                            </button>
                        </div>
                    </div>
                );

            case 6:
                return (
                    <div className="signup-step">
                        <h2>Create a Username</h2>
                        <div className="username-input-container">
                            <input
                                type="text"
                                className={`username-input ${usernameStatus.available === false ? 'input-error' : ''} ${usernameStatus.available === true ? 'input-success' : ''}`}
                                value={signupData.username}
                                onChange={(e) => handleSignupChange('username', e.target.value)}
                                placeholder="Enter your username"
                            />
                            <div className="username-status-indicator">
                                {usernameStatus.checking && (
                                    <div className="spinner"></div>
                                )}
                                {usernameStatus.available === true && (
                                    <span className="status-icon success">✓</span>
                                )}
                            </div>
                        </div>
                        {usernameStatus.available === false && (
                            <div className="username-error-message">{usernameStatus.message}</div>
                        )}

                        <div className="button-group">
                            <button className="back-button" onClick={prevStep}>
                                <FaArrowLeft />
                            </button>
                            <button
                                className="continue-button"
                                onClick={() => {
                                    // Add auth parameter
                                    navigate('?auth=login', { replace: true });
                                    setModal('auth');
                                }}
                                disabled={!signupData.username || usernameStatus.available === false || usernameStatus.checking}
                            >
                                Continue <FaArrowRight />
                            </button>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    if (isAuthenticated) {
        navigate('/');
        return null;
    }

    return (
        <div className="welcome-page">
            <header className="hero">
                <div className="hero-content">
                    <div className="hero-left">
                        <img
                            src={slide5Transparent}
                            alt="Hero"
                        />
                    </div>
                    <div className="hero-right">
                        <div className="hero-text">
                            <h1>
                                <span className="langjee-brand">Langjee</span>
                                <span className="langjee-tagline">The Laziest Way to Learn a Language</span>
                            </h1>
                            <p>No flashcards, no burden, just progress.</p>
                        </div>
                        <div className="hero-buttons">
                            <button className="btn-primary" onClick={() => openModal('signup')}>
                                Get Started for Free
                            </button>
                            <button className="btn-secondary" onClick={() => openModal('auth')}>
                                I already have an account
                            </button>
                        </div>
                    </div>
                    <div className="hero-buttons-container">
                        <div className="hero-buttons">
                            <button className="btn-primary" onClick={() => openModal('signup')}>
                                Get Started for Free
                            </button>
                            <button className="btn-secondary" onClick={() => openModal('auth')}>
                                I already have an account
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <div className="sections-container">
                <section className="section">
                    <div className="section-content">
                        <h2>Master the Language Through Sentences</h2>
                        <p>
                            Forget word lists. Language lives in context. Langjee trains you through high-quality sentence repetition, targeting vocab and core grammar structures. Japanese, Korean, and Chinese learners get grammar-focused sentences. Spanish, French, and Italian users can drill verb tenses and endings by category. Every sentence comes with definitions, grammar tips, and native-quality audio.
                        </p>
                    </div>
                    <div className="section-img">
                        <img
                            src={sentences2}
                            alt="Master the Language Through Sentences"
                        />
                    </div>
                </section>

                <section className="section">
                    <div className="section-content">
                        <h2>A Repetition System That Actually Works</h2>
                        <p>
                            Our custom-built audio player automates what used to take hours. Shuffle mode, custom repeat counts, variable playback speed for each loop, adjustable pause lengths—it's all designed to give you time to actually speak. Sentences are organized into rotating playlists with automatic review cycles, so you never have to micromanage your study load.
                        </p>
                    </div>
                    <div className="section-img">
                        <img
                            src={audioPlayer1}
                            alt="A Repetition System That Actually Works"
                        />
                    </div>
                </section>

                <section className="section">
                    <div className="section-content">
                        <h2>Learn Through Stories—On Your Terms</h2>
                        <p>
                            Daily Stories match your current level and include an intuitive context-based dictionary. Click any word for meaning, usage, and grammar. Save and grade words or characters; they'll be highlighted in future stories and even reinforced in sentence drills. Set how often saved words appear, or turn it off completely—it's your call.
                        </p>
                    </div>
                    <div className="section-img">
                        <img
                            src={dailystory}
                            alt="Learn Through Stories—On Your Terms"
                        />
                    </div>
                </section>

                <section className="section">
                    <div className="section-content">
                        <h2>Chat Freely with Bojee or Your Own AI Partner</h2>
                        <p>
                            Practice conversations in your target language with Bojee, an AI tutor who offers light corrections and remembers details to keep interactions natural. Or build your own custom chat partner—no restrictions, no censors. All chats come with the same word lookup and highlighting system as Daily Stories, reinforcing what you've saved.
                        </p>
                    </div>
                    <div className="section-img">
                        <img
                            src={slide4Final}
                            alt="Chat Freely with Bojee or Your Own AI Partner"
                        />
                    </div>
                </section>

                <section className="section">
                    <div className="section-content">
                        <h2>Efficiency-First Design</h2>
                        <p>
                            Langjee is built for lazy people who still want results. Every feature cuts friction. From auto-sorting sentence playlists to one-tap sentence generation, the app is designed so that you can show up, hit play, and learn—without wasting a second.
                        </p>
                    </div>
                    <div className="section-img">
                        <img
                            src={slide6}
                            alt="Efficiency-First Design"
                        />
                    </div>
                </section>
            </div>

            <section className="footer-cta">
                <h2>Try Langjee Today</h2>
                <p>
                    Ready to ditch inefficiency and embrace effortless language learning? Join
                    now and experience a smarter way to learn.
                </p>
                <button className="btn-primary" onClick={() => openModal('signup')}>
                    Get Started for Free
                </button>
            </section>

            <footer className="footer">
                <div className="footer-content">
                    <div className="footer-copyright">
                        &copy; {new Date().getFullYear()} Langjee. All rights reserved.
                    </div>
                    <div className="footer-links">
                        <Link to="/privacy-policy">Privacy Policy</Link>
                        <span className="footer-divider">|</span>
                        <Link to="/terms-of-service">Terms of Service</Link>
                    </div>
                </div>
            </footer>

            {modal === 'auth' && (
                <FirebaseAuthModal
                    onClose={closeModal}
                    onAuthenticated={handleAuthenticated}
                    initialMode={signupStep === 6 ? 'signup' : 'login'}
                    initialEmail=""
                    openSignup={() => {
                        navigate('?auth=signup', { replace: true });
                        setModal('signup');
                    }}
                />
            )}

            {modal === 'signup' && (
                <div className="fullscreen-overlay" onClick={closeModal}>
                    <div className="fullscreen-content" onClick={(e) => e.stopPropagation()}>
                        <button className="close-fullscreen-button" onClick={closeModal}>×</button>
                        {renderSignupContent()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default WelcomePage;
